import { BudgetDto, BudgetGeneralityDto, BudgetGeneralityFieldDto } from 'api/budgets/models/BudgetDto';
import { BudgetGeneralityDefaultDto } from 'api/budgetGeneralityDefaults/models/BudgetGeneralityDefaultDto';
import { TaxDto } from 'api/taxes/models/TaxDto';
import { UnitDto } from 'api/units/models/UnitDto';
import Button from 'common/components/button/Button';
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'common/components/modal/Modal';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import MoneyInput from 'common/components/moneyInput/MoneyInput';
import SelectInput from 'common/components/selectInput/SelectInput';
import TextInput from 'common/components/textInput/TextInput';
import InputError from 'common/components/inputError/InputError';
import ExpressionSystem, { ExpressionParameter } from 'common/components/expressionButton/ExpressionSystem';
import CheckInput from 'common/components/checkInput/CheckInput';
import { Header, Label } from 'common/components/texts/Texts';
import newGuid from 'common/utils/newGuid';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { CSSProperties, Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FaAngleDown, FaAngleRight, FaPlus, FaTrash } from 'react-icons/fa';
import { IoMoveOutline } from 'react-icons/io5';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import store from 'store/store';
import { Reducers } from 'store/types';
import { calculateBudgetTotals, updateBudget } from 'store/budget/action';
import { DEFAULT_DECIMAL_PLACES } from 'Config';
import styles from './BudgetGeneralities.module.scss';

interface Props {
    isDetails: boolean;
    units: UnitDto[];
    taxes: TaxDto[];
    // Generalidades por defeito do backoffice, disponíveis para importação manual.
    generalityDefaults: BudgetGeneralityDefaultDto[];
    // Converte uma generalidade por defeito numa linha materializada (expressões avaliadas com os
    // totais atuais do orçamento e a designação na língua do orçamento).
    materializeDefault: (d: BudgetGeneralityDefaultDto) => BudgetGeneralityDto;
}

export function pickGeneralityDesignation(
    translations: { languageIso: string; designation: string }[] | undefined,
    iso: string | undefined | null,
): string {
    if (!translations || translations.length === 0) return '';
    const match = translations.find(tr => tr.languageIso === iso);
    return match?.designation ?? translations[0].designation ?? '';
}

// Limite de passagens de reavaliação por "rajada". Fórmulas que referenciam o total combinado
// (linhas + generalidades) são auto-referenciais: convergem normalmente em poucas passagens, mas
// uma fórmula divergente (ex.: quantidade = total combinado) entraria em ciclo infinito sem este
// travão.
const REEVAL_MAX_PASSES = 50;
// Tolerância para considerar que uma reavaliação convergiu (evita oscilações no último dígito).
const REEVAL_EPSILON = 1e-6;
// Limite de caracteres da designação da generalidade e dos nomes dos campos (apenas frontend).
export const GENERALITY_MAX_TEXT_LENGTH = 250;

export function calculateGeneralityTotals(quantity: number | null | undefined, unitPrice: number, taxValue: number | undefined | null) {
    const qty = quantity ?? 0;
    const totalExcludingTax = qty * (unitPrice ?? 0);
    const taxRate = (taxValue ?? 0) / 100;
    const totalTax = totalExcludingTax * taxRate;
    const totalIncludingTax = totalExcludingTax + totalTax;
    return { totalExcludingTax, totalTax, totalIncludingTax };
}

// Materializa uma generalidade por defeito do backoffice numa linha de orçamento, avaliando as
// expressões (campos personalizados primeiro, depois quantidade/preço) com os totais do orçamento
// fornecidos. Usado tanto no carregamento (pré-preenchimento de orçamentos novos) como na
// importação manual pelo botão "Adicionar generalidade". Os parâmetros de orçamento devem usar os
// totais "lines-only" para evitar tornar as expressões auto-referenciais.
export function buildBudgetGeneralityFromDefault(
    d: BudgetGeneralityDefaultDto,
    budgetExpressionParams: ExpressionParameter[],
    defaultTaxId: string | null,
    defaultTaxValue: number,
    pickDesignation: (translations: { languageIso: string; designation: string }[] | undefined) => string,
): BudgetGeneralityDto {
    // Materializa os campos personalizados: avalia cada expressão com os totais do orçamento + os
    // campos já calculados (referências só à própria generalidade, via prefixo "self").
    const computedFields: NonNullable<BudgetGeneralityDto['fields']> = [];
    for (const f of (d.fields ?? []).slice().sort((a, b) => a.position - b.position)) {
        const fieldParams = [
            ...budgetExpressionParams,
            ...computedFields.map(cf => ({
                id: ExpressionSystem.formatParameterId(['self', 'field', cf.fieldKey]),
                name: '',
                value: cf.value ?? 0,
            })),
        ];
        const fieldValue = f.valueExpression
            ? (() => {
                const r = ExpressionSystem.evaluate(f.valueExpression, fieldParams);
                return r && !r.error && typeof r.value === 'number' ? r.value : 0;
            })()
            : (f.value ?? 0);
        computedFields.push({
            id: null,
            position: f.position,
            fieldKey: f.fieldKey,
            designation: pickDesignation(f.translations),
            value: fieldValue,
            valueExpression: f.valueExpression ?? null,
            unitId: f.unitId ?? null,
            unitSymbol: f.unitSymbol ?? null,
            showInPdf: f.showInPdf,
        });
    }

    const selfFieldParams = [
        ...budgetExpressionParams,
        ...computedFields.map(cf => ({
            id: ExpressionSystem.formatParameterId(['self', 'field', cf.fieldKey]),
            name: '',
            value: cf.value ?? 0,
        })),
    ];
    const evalSelf = (expr: string | null | undefined): number | null => {
        if (!expr) return null;
        const r = ExpressionSystem.evaluate(expr, selfFieldParams);
        return r && !r.error && typeof r.value === 'number' ? r.value : null;
    };

    const quantity = d.quantityExpression ? evalSelf(d.quantityExpression) : (d.quantity ?? null);
    const unitPrice = d.unitPriceExpression ? (evalSelf(d.unitPriceExpression) ?? 0) : (d.unitPrice ?? 0);
    const totals = calculateGeneralityTotals(quantity, unitPrice, defaultTaxValue);
    return {
        id: null,
        designation: pickDesignation(d.translations),
        quantity,
        quantityExpression: d.quantityExpression ?? null,
        unitId: d.unitId ?? null,
        unitSymbol: d.unitSymbol ?? null,
        unitPrice,
        unitPriceExpression: d.unitPriceExpression ?? null,
        taxId: defaultTaxId,
        taxValue: defaultTaxValue,
        totalExcludingTax: totals.totalExcludingTax,
        totalTax: totals.totalTax,
        totalIncludingTax: totals.totalIncludingTax,
        sourceGeneralityDefaultId: d.id,
        fields: computedFields,
    };
}

function BudgetGeneralities({ isDetails, units, taxes, generalityDefaults, materializeDefault }: Props): JSX.Element {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const form = useFormContext<BudgetDto>();
    const decimalPlaces = useSelector<Reducers, number>(s => s.budget.budget.decimalPlaces ?? DEFAULT_DECIMAL_PLACES);
    const { fields, append, remove, move } = useFieldArray({
        control: form.control,
        name: 'generalities',
        keyName: '_key',
    });

    const watchedGeneralities = useWatch({ control: form.control, name: 'generalities' }) ?? [];

    const generalitySensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    const onGeneralityDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = fields.findIndex(f => f._key === active.id);
        const newIndex = fields.findIndex(f => f._key === over.id);
        if (oldIndex >= 0 && newIndex >= 0) {
            move(oldIndex, newIndex);
        }
    };

    // Assinatura dos valores referenciáveis de cada generalidade (quantidade, preço unitário e
    // totais). Quando qualquer um muda, as expressões que referenciam outras linhas são
    // reavaliadas. Os totais "lines-only" do orçamento não cobrem este caso (ver budgetTotalsKey).
    const generalitiesSignature = watchedGeneralities
        .map(g => `${g?.quantity ?? ''}:${g?.unitPrice ?? ''}:${g?.totalExcludingTax ?? ''}:${g?.totalTax ?? ''}:${g?.totalIncludingTax ?? ''}`)
        .join('|');

    // Assinatura dos valores/expressões dos campos personalizados de cada generalidade. Quando um
    // campo (ou a sua fórmula) muda, é preciso reavaliar os campos e, por arrasto, a quantidade/preço
    // que os referenciam.
    const fieldsSignature = watchedGeneralities
        .map(g => (g?.fields ?? []).map(f => `${f.fieldKey}=${f.value ?? ''}:${f.valueExpression ?? ''}`).join(','))
        .join('|');

    // Linhas de generalidade expandidas (mostram os campos personalizados). Indexado por _key.
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

    // Modal "Adicionar generalidade": permite importar generalidades do backoffice (multi-seleção)
    // ou criar uma linha vazia. As generalidades já não são injetadas automaticamente ao reabrir um
    // orçamento — são sempre adicionadas explicitamente aqui.
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [selectedDefaultIds, setSelectedDefaultIds] = useState<Record<string, boolean>>({});

    const openAddModal = () => {
        setSelectedDefaultIds({});
        setIsAddModalOpen(true);
    };

    // Defaults já presentes no orçamento (por origem) — apenas para assinalar na lista de importação;
    // não impede reimportar.
    const presentDefaultIds = new Set(
        watchedGeneralities.map(g => g?.sourceGeneralityDefaultId).filter((x): x is string => !!x)
    );
    const hasSelectedDefaults = Object.values(selectedDefaultIds).some(Boolean);

    const toggleExpanded = (key: string) => {
        setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Marca updates programáticos (vindos do modal de expressão ou da auto-reavaliação) para o
    // onChange do MoneyInput os ignorar e não limpar a expressão recém-guardada.
    const skipExpressionClearRef = useRef<Record<string, boolean>>({});

    // Conta as passagens de reavaliação consecutivas para travar fórmulas auto-referenciais
    // divergentes (ver REEVAL_MAX_PASSES). É reposto a zero sempre que a reavaliação converge.
    const reevalPassesRef = useRef(0);

    // Totais do orçamento apenas das linhas (sem generalidades) — usados pela opção
    // "Total Linhas sem IVA" e para reavaliar quando uma linha muda.
    const budgetTotalsKey = useSelector<Reducers, string>(s => {
        const b: any = s.budget.budget;
        return `${b.linesTotalNetExcludingTax ?? b.totalNetExcludingTax ?? 0}|${b.linesTotalExcludingTax ?? b.totalExcludingTax ?? 0}|${b.linesTotalTax ?? b.totalTax ?? 0}|${b.linesTotalIncludingTax ?? b.totalIncludingTax ?? 0}|${b.defaultTaxValue ?? 0}`;
    });

    // Totais canónicos (linhas + generalidades) — refletem os valores reais do rodapé e são o que
    // a opção "Total líquido sem IVA" devolve nas fórmulas. Disparam reavaliação quando mudam.
    const combinedTotalsKey = useSelector<Reducers, string>(s => {
        const b: any = s.budget.budget;
        return `${b.totalNetExcludingTax ?? 0}|${b.totalExcludingTax ?? 0}|${b.totalTax ?? 0}|${b.totalIncludingTax ?? 0}`;
    });

    useEffect(() => {
        // Em modo leitura (detalhes/versão) não reavaliamos expressões: têm de aparecer
        // exatamente os valores gravados. Reavaliar aqui faria o subtotal — e, por arrasto,
        // o "Total s/ IVA" do cabeçalho — saltar de valor só por abrir a tab.
        if (isDetails) return;

        const generalities = form.getValues('generalities') ?? [];
        const updates: Array<{ idx: number; quantity: number | null; unitPrice: number; totals: ReturnType<typeof calculateGeneralityTotals> }> = [];

        // 1) Reavaliar os campos personalizados primeiro: podem depender dos totais do orçamento e
        // de campos irmãos. Aplicados de imediato para que campos seguintes (e a quantidade/preço)
        // vejam os valores atualizados.
        let anyFieldChanged = false;
        generalities.forEach((g, idx) => {
            (g.fields ?? []).forEach((f, fIdx) => {
                if (!f.valueExpression) return;
                const params = buildFieldExpressionParameters(idx, f.fieldKey);
                const r = ExpressionSystem.evaluate(f.valueExpression, params);
                if (r && !r.error && typeof r.value === 'number' && Math.abs(r.value - (f.value ?? 0)) > REEVAL_EPSILON) {
                    skipExpressionClearRef.current[`${idx}:field:${f.fieldKey}`] = true;
                    form.setValue(`generalities.${idx}.fields.${fIdx}.value`, r.value);
                    anyFieldChanged = true;
                }
            });
        });

        // 2) Reavaliar quantidade/preço (podem referenciar os campos acabados de calcular).
        generalities.forEach((g, idx) => {
            if (!g.quantityExpression && !g.unitPriceExpression) return;

            const params = buildExpressionParameters(idx);

            let newQuantity: number | null = g.quantity ?? null;
            let newUnitPrice: number = g.unitPrice ?? 0;
            let changed = false;

            if (g.quantityExpression) {
                const r = ExpressionSystem.evaluate(g.quantityExpression, params);
                if (r && !r.error && typeof r.value === 'number' && Math.abs(r.value - (g.quantity ?? 0)) > REEVAL_EPSILON) {
                    newQuantity = r.value;
                    changed = true;
                }
            }
            if (g.unitPriceExpression) {
                const r = ExpressionSystem.evaluate(g.unitPriceExpression, params);
                if (r && !r.error && typeof r.value === 'number' && Math.abs(r.value - (g.unitPrice ?? 0)) > REEVAL_EPSILON) {
                    newUnitPrice = r.value;
                    changed = true;
                }
            }

            if (!changed) return;

            updates.push({ idx, quantity: newQuantity, unitPrice: newUnitPrice, totals: calculateGeneralityTotals(newQuantity, newUnitPrice, g.taxValue) });
        });

        if (updates.length === 0 && !anyFieldChanged) {
            reevalPassesRef.current = 0; // convergiu
            return;
        }

        // Travão de ciclo para fórmulas auto-referenciais divergentes (ex.: uma generalidade cuja
        // quantidade = "Total líquido sem IVA", que se inclui a si própria). Os casos convergentes
        // estabilizam bem antes do limite.
        reevalPassesRef.current += 1;
        if (reevalPassesRef.current > REEVAL_MAX_PASSES) {
            reevalPassesRef.current = 0;
            // eslint-disable-next-line no-console
            console.warn('Generalidades: reavaliação de fórmulas interrompida (possível referência circular).');
            return;
        }

        updates.forEach(u => {
            // Bloqueia o cascade que limparia as expressões
            skipExpressionClearRef.current[`${u.idx}:quantity`] = true;
            skipExpressionClearRef.current[`${u.idx}:unitPrice`] = true;

            form.setValue(`generalities.${u.idx}.quantity`, u.quantity);
            form.setValue(`generalities.${u.idx}.unitPrice`, u.unitPrice);
            form.setValue(`generalities.${u.idx}.totalExcludingTax`, u.totals.totalExcludingTax);
            form.setValue(`generalities.${u.idx}.totalTax`, u.totals.totalTax);
            form.setValue(`generalities.${u.idx}.totalIncludingTax`, u.totals.totalIncludingTax);
        });
    }, [budgetTotalsKey, combinedTotalsKey, generalitiesSignature, fieldsSignature, isDetails]);

    const unitOptions = units.map(u => ({ value: u.id, label: u.name }));
    const taxOptions = taxes.map(tx => ({ value: tx.id, label: `${tx.description ?? ''} (${tx.value ?? 0}%)` }));

    const onCreateBlankLine = () => {
        const defaultTaxId = form.getValues('defaultTaxId') ?? null;
        const defaultTax = taxes.find(tx => tx.id === defaultTaxId);
        const newLine: BudgetGeneralityDto = {
            id: null,
            designation: '',
            quantity: null,
            unitId: null,
            unitSymbol: null,
            unitPrice: 0,
            taxId: defaultTaxId ?? null,
            taxValue: defaultTax?.value ?? 0,
            totalExcludingTax: 0,
            totalTax: 0,
            totalIncludingTax: 0,
            fields: [],
        };
        append(newLine);
        setIsAddModalOpen(false);
    };

    const onImportSelectedDefaults = () => {
        const toImport = generalityDefaults.filter(d => selectedDefaultIds[d.id]);
        if (toImport.length === 0) return;
        const ordered = toImport.slice().sort((a, b) => a.order - b.order);
        // Se um default importado foi previamente dispensado neste orçamento, deixa de o estar.
        const dismissed = form.getValues('dismissedGeneralityDefaultIds') ?? [];
        const importedIds = new Set(ordered.map(d => d.id));
        const remainingDismissed = dismissed.filter(idn => !importedIds.has(idn));
        if (remainingDismissed.length !== dismissed.length) {
            form.setValue('dismissedGeneralityDefaultIds', remainingDismissed);
        }
        append(ordered.map(d => materializeDefault(d)));
        setIsAddModalOpen(false);
    };

    const onRemoveLine = (index: number) => {
        const item = form.getValues(`generalities.${index}`);
        const sourceId = item?.sourceGeneralityDefaultId;
        if (sourceId) {
            const current = form.getValues('dismissedGeneralityDefaultIds') ?? [];
            if (!current.includes(sourceId)) {
                form.setValue('dismissedGeneralityDefaultIds', [...current, sourceId]);
            }
        }
        remove(index);
    };

    const buildExpressionParameters = (currentIndex: number, excludeField?: 'quantity' | 'unitPrice', excludeFieldKey?: string): ExpressionParameter[] => {
        const params: ExpressionParameter[] = [];
        // Totais do orçamento vêm do Redux (mantido em sincronia pelo motor de cálculo de linhas).
        // O form pode ter valores desatualizados.
        const reduxBudget = store.getState().budget.budget;
        const form_ = form.getValues();
        const generalities = form.getValues('generalities') ?? [];

        const budgetName = t('budgets.budget.budget');
        const budgetKey = 'budget';
        // "Total líquido sem IVA" (e restantes totais do orçamento) refletem o valor real do
        // rodapé, ou seja, linhas + generalidades. "Total Linhas sem IVA" expõe o total apenas
        // das linhas — usar este último quando se quer evitar incluir as generalidades (e a
        // consequente auto-referência) numa expressão de generalidade.
        const budgetTotals: Array<[string, string, number | null | undefined]> = [
            ['totalNetExcludingTax', t('budgets.budget.lines.totals.total_net_without_tax'), reduxBudget.totalNetExcludingTax],
            ['linesTotalNetExcludingTax', t('budgets.budget.lines.totals.total_lines_without_tax'), reduxBudget.linesTotalNetExcludingTax ?? reduxBudget.totalNetExcludingTax],
            ['totalExcludingTax', t('budgets.budget.lines.totals.total_without_tax'), reduxBudget.totalExcludingTax],
            ['totalTax', t('budgets.budget.lines.totals.total_tax'), reduxBudget.totalTax],
            ['totalIncludingTax', t('budgets.budget.lines.totals.total_with_tax'), reduxBudget.totalIncludingTax],
            ['defaultTaxValue', t('budgets.budget.default_tax'), reduxBudget.defaultTaxValue ?? form_.defaultTaxValue ?? 0],
            ['defaultMarginPercentage', t('budgets.budget.default_margin'), reduxBudget.defaultMarginPercentage ?? 0],
            ['defaultCommissionMarginPercentage', t('budgets.budget.default_commission_margin'), reduxBudget.defaultCommissionMarginPercentage ?? 0],
            ['defaultCombinedMarginPercentage', t('budgets.budget.default_combined_margin'), (reduxBudget.defaultMarginPercentage ?? 0) + (reduxBudget.defaultCommissionMarginPercentage ?? 0)],
        ];
        for (const [id, name, value] of budgetTotals) {
            params.push({
                id: ExpressionSystem.formatParameterId([budgetKey, id]),
                name: ExpressionSystem.formatParameterName([budgetName, name]),
                value: value ?? 0,
            });
        }

        const generalityName = t('budgets.budget.tab_generalities');
        const currentLineLabel = t('budgets.budget.generalities.current_line');

        generalities.forEach((g, idx) => {
            const isCurrent = idx === currentIndex;
            // Linha atual usa o prefixo "self" (compatível com fórmulas definidas no backoffice).
            // Outras linhas usam um prefixo único por índice.
            const lineKey = isCurrent ? 'self' : `generality_${idx}`;
            const designation = isCurrent
                ? currentLineLabel
                : (g.designation || t('budgets.budget.generalities.header.designation') + ` #${idx + 1}`);

            const lineFields: Array<[string, string, number | null | undefined, 'quantity' | 'unitPrice' | undefined]> = [
                ['quantity', t('budgets.budget.generalities.header.quantity'), g.quantity, 'quantity'],
                ['unitPrice', t('budgets.budget.generalities.header.unit_price'), g.unitPrice, 'unitPrice'],
                ['totalExcludingTax', t('budgets.budget.generalities.line_total.excluding_tax'), g.totalExcludingTax, undefined],
                ['totalTax', t('budgets.budget.generalities.line_total.tax'), g.totalTax, undefined],
                ['totalIncludingTax', t('budgets.budget.generalities.line_total.including_tax'), g.totalIncludingTax, undefined],
            ];

            for (const [id, name, value, fieldKind] of lineFields) {
                if (isCurrent && fieldKind === excludeField) continue;
                if (isCurrent && fieldKind === undefined) continue;
                params.push({
                    id: ExpressionSystem.formatParameterId([lineKey, id]),
                    name: ExpressionSystem.formatParameterName([generalityName, designation, name]),
                    value: value ?? 0,
                });
            }

            // Campos personalizados de QUALQUER generalidade ficam disponíveis: a própria usa o
            // prefixo "self|field|<key>" (compatível com fórmulas do backoffice), as outras usam
            // "generality_<idx>|field|<key>". Exclui-se apenas o próprio campo a ser editado
            // (excludeFieldKey), para evitar auto-referência.
            for (const fld of g.fields ?? []) {
                if (isCurrent && excludeFieldKey && fld.fieldKey === excludeFieldKey) continue;
                params.push({
                    id: ExpressionSystem.formatParameterId([lineKey, 'field', fld.fieldKey]),
                    name: ExpressionSystem.formatParameterName([generalityName, designation, fld.designation || fld.fieldKey]),
                    value: fld.value ?? 0,
                });
            }
        });

        return params;
    };

    // Parâmetros disponíveis na fórmula de um campo: tudo o que a quantidade/preço pode referenciar
    // (totais do orçamento + quantidade/preço/campos de todas as generalidades), excluindo o próprio
    // campo a ser editado para evitar auto-referência.
    const buildFieldExpressionParameters = (generalityIndex: number, excludeFieldKey: string): ExpressionParameter[] =>
        buildExpressionParameters(generalityIndex, undefined, excludeFieldKey);

    const recalculate = (index: number, patch: Partial<BudgetGeneralityDto>) => {
        const current = form.getValues(`generalities.${index}`);
        const merged: BudgetGeneralityDto = { ...current, ...patch };
        const tax = taxes.find(tx => tx.id === merged.taxId);
        const taxValue = tax?.value ?? merged.taxValue ?? 0;
        const totals = calculateGeneralityTotals(merged.quantity, merged.unitPrice, taxValue);

        form.setValue(`generalities.${index}.taxValue`, taxValue);
        form.setValue(`generalities.${index}.totalExcludingTax`, totals.totalExcludingTax);
        form.setValue(`generalities.${index}.totalTax`, totals.totalTax);
        form.setValue(`generalities.${index}.totalIncludingTax`, totals.totalIncludingTax);
        if ('unitSymbol' in patch) {
            form.setValue(`generalities.${index}.unitSymbol`, patch.unitSymbol ?? null);
        }
        if ('quantityExpression' in patch) {
            form.setValue(`generalities.${index}.quantityExpression`, patch.quantityExpression ?? null);
        }
        if ('unitPriceExpression' in patch) {
            form.setValue(`generalities.${index}.unitPriceExpression`, patch.unitPriceExpression ?? null);
        }
    };

    const subtotalExcludingTax = fields.reduce((acc, _, i) => acc + (form.watch(`generalities.${i}.totalExcludingTax`) ?? 0), 0);
    const subtotalTax = fields.reduce((acc, _, i) => acc + (form.watch(`generalities.${i}.totalTax`) ?? 0), 0);
    const subtotalIncludingTax = fields.reduce((acc, _, i) => acc + (form.watch(`generalities.${i}.totalIncludingTax`) ?? 0), 0);

    // Mantém os subtotais das generalidades em sincronia com o Redux para que os totais do
    // orçamento (rodapé, total do cabeçalho e valor gravado) incluam as generalidades.
    useEffect(() => {
        const b = store.getState().budget.budget;
        if ((b.generalitiesTotalExcludingTax ?? 0) === subtotalExcludingTax && (b.generalitiesTotalTax ?? 0) === subtotalTax) {
            return;
        }
        dispatch(updateBudget({
            generalitiesTotalExcludingTax: subtotalExcludingTax,
            generalitiesTotalTax: subtotalTax,
        }));
        dispatch(calculateBudgetTotals());
    }, [subtotalExcludingTax, subtotalTax, dispatch]);

    return (
        <div className={styles.container}>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={styles.expandCol}></th>
                            <th className={styles.designationCol}>{t('budgets.budget.generalities.header.designation')}</th>
                            <th className={styles.quantityCol}>{t('budgets.budget.generalities.header.quantity')}</th>
                            <th className={styles.unitCol}>{t('budgets.budget.generalities.header.unit')}</th>
                            <th className={styles.priceCol}>{t('budgets.budget.generalities.header.unit_price')}</th>
                            <th className={styles.taxCol}>{t('budgets.budget.generalities.header.tax')}</th>
                            <th className={styles.totalCol}>{t('budgets.budget.generalities.header.total')}</th>
                            {!isDetails && <th className={styles.actionCol}></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {fields.length === 0 && (
                            <tr>
                                <td colSpan={isDetails ? 7 : 8} className={styles.empty}>
                                    {t('budgets.budget.generalities.empty')}
                                </td>
                            </tr>
                        )}
                        <DndContext sensors={generalitySensors} collisionDetection={closestCenter} onDragEnd={onGeneralityDragEnd} accessibility={{ container: document.body }}>
                            <SortableContext items={fields.map(f => f._key)} strategy={verticalListSortingStrategy}>
                                {fields.map((field, index) => {
                                    const isExpanded = !!expandedRows[field._key];
                                    return (
                                        <Fragment key={field._key}>
                                            <SortableTr id={field._key} disabled={isDetails}>
                                                {({ setNodeRef, style, handleProps }) => (
                                                    <tr ref={setNodeRef} style={style}>
                                                        <td className={styles.expandCol}>
                                                            <button
                                                                type='button'
                                                                className={styles.expandButton}
                                                                onClick={() => toggleExpanded(field._key)}
                                                                title={t('budgets.budget.generalities.fields.toggle')}
                                                            >
                                                                {isExpanded ? <FaAngleDown /> : <FaAngleRight />}
                                                            </button>
                                                        </td>
                                                        <td>
                                                            <Controller
                                                                control={form.control}
                                                                name={`generalities.${index}.sourceGeneralityDefaultId`}
                                                                render={() => <></>}
                                                            />
                                                            {/* Mantém os campos personalizados registados (e portanto gravados) mesmo
                                                                com a linha fechada — o editor aninhado só os regista quando expandido. */}
                                                            {!isExpanded && (
                                                                <Controller
                                                                    control={form.control}
                                                                    name={`generalities.${index}.fields`}
                                                                    render={() => <></>}
                                                                />
                                                            )}
                                                            <Controller
                                                                control={form.control}
                                                                name={`generalities.${index}.designation`}
                                                                rules={{ maxLength: GENERALITY_MAX_TEXT_LENGTH }}
                                                                render={({ field: f, fieldState }) => (
                                                                    <>
                                                                        <TextInput
                                                                            value={f.value ?? ''}
                                                                            onChange={e => f.onChange(e.target.value)}
                                                                            disabled={isDetails}
                                                                            hasError={Boolean(fieldState.error)}
                                                                        />
                                                                        <InputError error={fieldState.error} maxLength={GENERALITY_MAX_TEXT_LENGTH} />
                                                                    </>
                                                                )}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Controller
                                                                control={form.control}
                                                                name={`generalities.${index}.quantity`}
                                                                render={({ field: f }) => (
                                                                    <MoneyInput
                                                                        value={f.value}
                                                                        onChange={(v) => {
                                                                            const skipKey = `${index}:quantity`;
                                                                            if (skipExpressionClearRef.current[skipKey]) {
                                                                                skipExpressionClearRef.current[skipKey] = false;
                                                                                return;
                                                                            }
                                                                            f.onChange(v ?? null);
                                                                            const current = form.getValues(`generalities.${index}.quantityExpression`);
                                                                            if (current) {
                                                                                form.setValue(`generalities.${index}.quantityExpression`, null);
                                                                            }
                                                                            recalculate(index, { quantity: v ?? null, quantityExpression: null });
                                                                        }}
                                                                        disabled={isDetails}
                                                                        decimalScale={decimalPlaces}
                                                                        hideUnitsDrop
                                                                        expressionButton={{
                                                                            title: t('budgets.budget.generalities.header.quantity'),
                                                                            value: watchedGeneralities[index]?.quantityExpression ?? '',
                                                                            loadParameters: () => buildExpressionParameters(index, 'quantity'),
                                                                            onChange: (expr, value) => {
                                                                                skipExpressionClearRef.current[`${index}:quantity`] = true;
                                                                                form.setValue(`generalities.${index}.quantityExpression`, expr);
                                                                                f.onChange(value ?? null);
                                                                                recalculate(index, { quantity: value ?? null, quantityExpression: expr });
                                                                            },
                                                                        }}
                                                                    />
                                                                )}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Controller
                                                                control={form.control}
                                                                name={`generalities.${index}.unitId`}
                                                                render={({ field: f }) => (
                                                                    <SelectInput
                                                                        options={unitOptions}
                                                                        value={f.value}
                                                                        onChange={(newId) => {
                                                                            f.onChange(newId);
                                                                            const sym = units.find(u => u.id === newId)?.symbol ?? null;
                                                                            recalculate(index, { unitId: newId, unitSymbol: sym });
                                                                        }}
                                                                        disabled={isDetails}
                                                                    />
                                                                )}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Controller
                                                                control={form.control}
                                                                name={`generalities.${index}.unitPrice`}
                                                                render={({ field: f }) => (
                                                                    <MoneyInput
                                                                        value={f.value}
                                                                        onChange={(v) => {
                                                                            const skipKey = `${index}:unitPrice`;
                                                                            if (skipExpressionClearRef.current[skipKey]) {
                                                                                skipExpressionClearRef.current[skipKey] = false;
                                                                                return;
                                                                            }
                                                                            f.onChange(v ?? 0);
                                                                            const current = form.getValues(`generalities.${index}.unitPriceExpression`);
                                                                            if (current) {
                                                                                form.setValue(`generalities.${index}.unitPriceExpression`, null);
                                                                            }
                                                                            recalculate(index, { unitPrice: v ?? 0, unitPriceExpression: null });
                                                                        }}
                                                                        disabled={isDetails}
                                                                        unitPrefix='€'
                                                                        decimalScale={decimalPlaces}
                                                                        hideUnitsDrop
                                                                        expressionButton={{
                                                                            title: t('budgets.budget.generalities.header.unit_price'),
                                                                            value: watchedGeneralities[index]?.unitPriceExpression ?? '',
                                                                            loadParameters: () => buildExpressionParameters(index, 'unitPrice'),
                                                                            onChange: (expr, value) => {
                                                                                skipExpressionClearRef.current[`${index}:unitPrice`] = true;
                                                                                form.setValue(`generalities.${index}.unitPriceExpression`, expr);
                                                                                f.onChange(value ?? 0);
                                                                                recalculate(index, { unitPrice: value ?? 0, unitPriceExpression: expr });
                                                                            },
                                                                        }}
                                                                    />
                                                                )}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Controller
                                                                control={form.control}
                                                                name={`generalities.${index}.taxId`}
                                                                render={({ field: f }) => (
                                                                    <SelectInput
                                                                        options={taxOptions}
                                                                        value={f.value}
                                                                        onChange={(newId) => {
                                                                            f.onChange(newId);
                                                                            recalculate(index, { taxId: newId });
                                                                        }}
                                                                        disabled={isDetails}
                                                                    />
                                                                )}
                                                            />
                                                        </td>
                                                        <td className={styles.totalCell}>
                                                            <MoneyFormat value={form.watch(`generalities.${index}.totalIncludingTax`) ?? 0} />
                                                        </td>
                                                        {!isDetails && (
                                                            <td className={styles.actionCol}>
                                                                <div className='d-flex gap-2 justify-content-end'>
                                                                    <Button type='button' variant='third' className='px-2' {...handleProps} title={t('common.move')}>
                                                                        <IoMoveOutline />
                                                                    </Button>
                                                                    <Button type='button' variant='danger' onClick={() => onRemoveLine(index)}>
                                                                        <FaTrash />
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                )}
                                            </SortableTr>
                                            {isExpanded && (
                                                <GeneralityFieldsEditor
                                                    key={`${field._key}-fields`}
                                                    generalityIndex={index}
                                                    isDetails={isDetails}
                                                    units={units}
                                                    decimalPlaces={decimalPlaces}
                                                    buildFieldExpressionParameters={buildFieldExpressionParameters}
                                                    skipExpressionClearRef={skipExpressionClearRef}
                                                />
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </SortableContext>
                        </DndContext>
                    </tbody>
                    {(fields.length > 0 || !isDetails) && (
                        <tfoot>
                            <tr>
                                {!isDetails && (
                                    <td colSpan={2} rowSpan={fields.length > 0 ? 3 : 1} className={styles.addLineCell}>
                                        <Button type='button' onClick={openAddModal}>
                                            <FaPlus /> {t('budgets.budget.generalities.add_line')}
                                        </Button>
                                    </td>
                                )}
                                {fields.length > 0 && (
                                    <>
                                        <td colSpan={isDetails ? 6 : 4} className={styles.subtotalLabel}>{t('budgets.budget.generalities.subtotal.excluding_tax')}</td>
                                        <td className={styles.totalCell}><MoneyFormat value={subtotalExcludingTax} /></td>
                                        {!isDetails && <td />}
                                    </>
                                )}
                                {fields.length === 0 && <td colSpan={6} />}
                            </tr>
                            {fields.length > 0 && (
                                <>
                                    <tr>
                                        <td colSpan={isDetails ? 6 : 4} className={styles.subtotalLabel}>{t('budgets.budget.generalities.subtotal.tax')}</td>
                                        <td className={styles.totalCell}><MoneyFormat value={subtotalTax} /></td>
                                        {!isDetails && <td />}
                                    </tr>
                                    <tr>
                                        <td colSpan={isDetails ? 6 : 4} className={styles.subtotalLabel}>{t('budgets.budget.generalities.subtotal.including_tax')}</td>
                                        <td className={styles.totalCell}><MoneyFormat value={subtotalIncludingTax} /></td>
                                        {!isDetails && <td />}
                                    </tr>
                                </>
                            )}
                        </tfoot>
                    )}
                </table>
            </div>

            <Modal
                isOpen={isAddModalOpen}
                onRequestClose={() => setIsAddModalOpen(false)}
                style={{ content: { minWidth: 520, maxWidth: 640 } }}
            >
                <div>
                    <ModalHeader>
                        <Header>{t('budgets.budget.generalities.add_modal.title').toUpperCase()}</Header>
                    </ModalHeader>
                    <ModalBody>
                        <Label space>{t('budgets.budget.generalities.add_modal.import_label')}</Label>
                        {generalityDefaults.length === 0
                            ? (
                                <div className={styles.empty}>{t('budgets.budget.generalities.add_modal.no_defaults')}</div>
                            )
                            : (
                                <div className={styles.importList}>
                                    {generalityDefaults.slice().sort((a, b) => a.order - b.order).map(d => {
                                        const alreadyAdded = presentDefaultIds.has(d.id);
                                        const label = d.designation || pickGeneralityDesignation(d.translations, undefined);
                                        return (
                                            <label key={d.id} className={styles.importItem}>
                                                <CheckInput
                                                    checked={!!selectedDefaultIds[d.id]}
                                                    onChange={e => setSelectedDefaultIds(prev => ({ ...prev, [d.id]: e.target.checked }))}
                                                />
                                                <span>
                                                    {label || t('budgets.budget.generalities.header.designation')}
                                                    {alreadyAdded && <span className={styles.alreadyAdded}> ({t('budgets.budget.generalities.add_modal.already_added')})</span>}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                    </ModalBody>
                    <ModalFooter className='d-flex justify-content-between'>
                        <Button type='button' variant='third' onClick={() => setIsAddModalOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <div className='d-flex gap-2'>
                            <Button type='button' variant='secondary' onClick={onCreateBlankLine}>
                                {t('budgets.budget.generalities.add_modal.create_new')}
                            </Button>
                            <Button type='button' onClick={onImportSelectedDefaults} disabled={!hasSelectedDefaults}>
                                {t('budgets.budget.generalities.add_modal.import_selected')}
                            </Button>
                        </div>
                    </ModalFooter>
                </div>
            </Modal>
        </div>
    );
}

interface GeneralityFieldsEditorProps {
    generalityIndex: number;
    isDetails: boolean;
    units: UnitDto[];
    decimalPlaces: number;
    buildFieldExpressionParameters: (generalityIndex: number, excludeFieldKey: string) => ExpressionParameter[];
    skipExpressionClearRef: { current: Record<string, boolean> };
}

function GeneralityFieldsEditor({ generalityIndex, isDetails, units, decimalPlaces, buildFieldExpressionParameters, skipExpressionClearRef }: GeneralityFieldsEditorProps): JSX.Element {
    const { t } = useTranslation();
    const form = useFormContext<BudgetDto>();
    const { fields, append, remove, move } = useFieldArray({
        control: form.control,
        name: `generalities.${generalityIndex}.fields`,
        keyName: '_key',
    });

    const watchedFields = useWatch({ control: form.control, name: `generalities.${generalityIndex}.fields` }) ?? [];
    const unitOptions = units.map(u => ({ value: u.id, label: u.name }));

    const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

    const onFieldsDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = fields.findIndex(f => f.fieldKey === active.id);
        const newIndex = fields.findIndex(f => f.fieldKey === over.id);
        if (oldIndex >= 0 && newIndex >= 0) {
            move(oldIndex, newIndex);
        }
    };

    const onAddField = () => {
        const newField: BudgetGeneralityFieldDto = {
            id: null,
            position: fields.length,
            fieldKey: newGuid(),
            designation: '',
            value: 0,
            valueExpression: null,
            unitId: null,
            unitSymbol: null,
            showInPdf: false,
        };
        append(newField);
    };

    const colsAfterExpand = isDetails ? 6 : 7;

    return (
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onFieldsDragEnd} accessibility={{ container: document.body }}>
            <tr>
                <td className={styles.expandCol} />
                <td colSpan={colsAfterExpand} className={styles.fieldsHeading}>
                    {t('budgets.budget.generalities.fields.title')}
                </td>
            </tr>
            {fields.length > 0 && (
                <tr>
                    <td className={styles.expandCol} />
                    <td className={styles.designationCol}><Label space>{t('budgets.budget.generalities.fields.name')}</Label></td>
                    <td className={styles.quantityCol}><Label space>{t('budgets.budget.generalities.fields.value')}</Label></td>
                    <td className={styles.unitCol}><Label space>{t('budgets.budget.generalities.fields.unit')}</Label></td>
                    <td className={styles.priceCol}><Label space>{t('budgets.budget.generalities.fields.show_in_pdf')}</Label></td>
                    <td className={styles.taxCol} />
                    <td className={styles.totalCol} />
                    {!isDetails && <td className={styles.actionCol} />}
                </tr>
            )}
            <SortableContext items={fields.map(f => f.fieldKey)} strategy={verticalListSortingStrategy}>
                {fields.map((field, fIdx) => {
                    const fieldKey = watchedFields[fIdx]?.fieldKey ?? field.fieldKey;
                    const skipKey = `${generalityIndex}:field:${fieldKey}`;
                    return (
                        <SortableTr key={field._key} id={field.fieldKey} disabled={isDetails}>
                            {({ setNodeRef, style, handleProps }) => (
                                <tr ref={setNodeRef} style={style}>
                                    <td className={styles.expandCol} />
                                    <td className={styles.designationCol}>
                                        <Controller
                                            control={form.control}
                                            name={`generalities.${generalityIndex}.fields.${fIdx}.fieldKey`}
                                            render={() => <></>}
                                        />
                                        <Controller
                                            control={form.control}
                                            name={`generalities.${generalityIndex}.fields.${fIdx}.designation`}
                                            rules={{ maxLength: GENERALITY_MAX_TEXT_LENGTH }}
                                            render={({ field: f, fieldState }) => (
                                                <>
                                                    <TextInput
                                                        value={f.value ?? ''}
                                                        onChange={e => f.onChange(e.target.value)}
                                                        disabled={isDetails}
                                                        hasError={Boolean(fieldState.error)}
                                                    />
                                                    <InputError error={fieldState.error} maxLength={GENERALITY_MAX_TEXT_LENGTH} />
                                                </>
                                            )}
                                        />
                                    </td>
                                    <td className={styles.quantityCol}>
                                        <Controller
                                            control={form.control}
                                            name={`generalities.${generalityIndex}.fields.${fIdx}.value`}
                                            render={({ field: f }) => (
                                                <MoneyInput
                                                    value={f.value}
                                                    onChange={(v) => {
                                                        if (skipExpressionClearRef.current[skipKey]) {
                                                            skipExpressionClearRef.current[skipKey] = false;
                                                            return;
                                                        }
                                                        f.onChange(v ?? 0);
                                                        const current = form.getValues(`generalities.${generalityIndex}.fields.${fIdx}.valueExpression`);
                                                        if (current) {
                                                            form.setValue(`generalities.${generalityIndex}.fields.${fIdx}.valueExpression`, null);
                                                        }
                                                    }}
                                                    disabled={isDetails}
                                                    decimalScale={decimalPlaces}
                                                    hideUnitsDrop
                                                    expressionButton={{
                                                        title: watchedFields[fIdx]?.designation || t('budgets.budget.generalities.fields.value'),
                                                        value: watchedFields[fIdx]?.valueExpression ?? '',
                                                        loadParameters: () => buildFieldExpressionParameters(generalityIndex, fieldKey),
                                                        onChange: (expr, value) => {
                                                            skipExpressionClearRef.current[skipKey] = true;
                                                            form.setValue(`generalities.${generalityIndex}.fields.${fIdx}.valueExpression`, expr);
                                                            f.onChange(value ?? 0);
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    </td>
                                    <td className={styles.unitCol}>
                                        <Controller
                                            control={form.control}
                                            name={`generalities.${generalityIndex}.fields.${fIdx}.unitId`}
                                            render={({ field: f }) => (
                                                <SelectInput
                                                    options={unitOptions}
                                                    value={f.value}
                                                    onChange={(newId) => {
                                                        f.onChange(newId);
                                                        const sym = units.find(u => u.id === newId)?.symbol ?? null;
                                                        form.setValue(`generalities.${generalityIndex}.fields.${fIdx}.unitSymbol`, sym);
                                                    }}
                                                    disabled={isDetails}
                                                />
                                            )}
                                        />
                                    </td>
                                    <td className={styles.priceCol}>
                                        <Controller
                                            control={form.control}
                                            name={`generalities.${generalityIndex}.fields.${fIdx}.showInPdf`}
                                            render={({ field: f }) => (
                                                <CheckInput
                                                    checked={!!f.value}
                                                    onChange={e => f.onChange(e.target.checked)}
                                                    disabled={isDetails}
                                                />
                                            )}
                                        />
                                    </td>
                                    <td className={styles.taxCol} />
                                    <td className={styles.totalCol} />
                                    {!isDetails && (
                                        <td className={styles.actionCol}>
                                            <div className='d-flex gap-2 justify-content-end'>
                                                <Button type='button' variant='third' className='px-2' {...handleProps} title={t('common.move')}>
                                                    <IoMoveOutline />
                                                </Button>
                                                <Button type='button' variant='danger' onClick={() => remove(fIdx)}>
                                                    <FaTrash />
                                                </Button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            )}
                        </SortableTr>
                    );
                })}
            </SortableContext>
            {!isDetails && (
                <tr>
                    <td className={styles.expandCol} />
                    <td colSpan={colsAfterExpand}>
                        <Button type='button' onClick={onAddField}>
                            <FaPlus /> {t('budgets.budget.generalities.fields.add')}
                        </Button>
                    </td>
                </tr>
            )}
        </DndContext>
    );
}

interface SortableTrProps {
    id: string;
    disabled?: boolean;
    children: (args: { setNodeRef: (node: HTMLElement | null) => void; style: CSSProperties; handleProps: Record<string, unknown> }) => JSX.Element;
}

function SortableTr({ id, disabled, children }: SortableTrProps): JSX.Element {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
    const style: CSSProperties = {
        // Lista vertical: anula o deslocamento horizontal para a linha só se mover para cima/baixo.
        transform: transform ? CSS.Translate.toString({ ...transform, x: 0 }) : undefined,
        transition,
        opacity: isDragging ? 0.4 : 1,
    };
    return children({ setNodeRef, style, handleProps: { ...attributes, ...listeners } });
}

export default BudgetGeneralities;
